class HormoneSmartQuiz extends HTMLElement {
  constructor() {
    super();
    this.currentQuestionIndex = 0;
    this.currentResultKey = '';
    this.answers = {};
  }

  connectedCallback() {
    this.screens = Array.from(this.querySelectorAll('[data-quiz-screen]'));
    this.questionScreens = Array.from(this.querySelectorAll('[data-quiz-question]'));
    this.progressFill = this.querySelector('[data-quiz-progress-fill]');
    this.progressStep = this.querySelector('[data-quiz-progress-step]');
    this.resultMount = this.querySelector('[data-quiz-result-mount]');
    this.resultKeyInput = this.querySelector('[data-quiz-result-key]');
    this.emailForms = Array.from(this.querySelectorAll('[data-quiz-email-form]'));
    this.shell = this.querySelector('.hormone-smart-quiz__shell');
    this.quizId = this.dataset.sectionId || 'hormone-smart-quiz';
    this.quizName = 'Hormone Smart Skin Profile';
    this.storageKey = `xomd:hormone-smart-quiz:${this.quizId}`;
    this.popupLeadStorageKey = 'xomd:quiz-popup-lead';
    this.storageVersion = 1;
    this.transitionOutDuration = 220;
    this.transitionInDuration = 360;
    this.heightReleaseTimer = null;
    this.heightSettleTimer = null;
    this.transitionTimer = null;
    this.knownKlaviyoProfilePromise = this.isKlaviyoProfileIdentified();

    this.addEventListener('click', this.handleClick.bind(this));
    this.emailForms.forEach((form) => {
      form.addEventListener('submit', this.handleEmailSubmit.bind(this));
      form.dataset.quizBound = 'true';
    });

    const restored = this.restoreSavedState();
    if (restored?.screenName === 'result') {
      this.renderResult(restored.resultKey || this.calculateResult(), { trackCompletion: false });
    }

    this.showScreen(restored?.screenName || 'intro');
    this.updateProgress();
    this.updateAllNextButtonStates();
    this.bindImageLoads();
    this.syncMediaHeights();

    window.addEventListener('resize', () => {
      window.requestAnimationFrame(() => this.syncMediaHeights());
    });
  }

  handleClick(event) {
    const option = event.target.closest('[data-quiz-option]');
    if (option && this.contains(option)) {
      this.toggleOption(option);
      return;
    }

    const action = event.target.closest('[data-quiz-action]');
    if (!action || !this.contains(action)) return;

    const actionName = action.dataset.quizAction;

    if (actionName === 'start') {
      this.currentQuestionIndex = 0;
      this.trackQuizEvent('xomd_quiz_start');
      this.showQuestion(0);
    }

    if (actionName === 'next') this.nextQuestion();
    if (actionName === 'back') this.previousQuestion();
    if (actionName === 'restart') this.restart();
    if (actionName === 'screen') {
      this.showScreen(action.dataset.quizTarget);
      this.saveProgress(action.dataset.quizTarget);
    }
  }

  toggleOption(option) {
    const screen = option.closest('[data-quiz-question]');
    const questionId = screen.dataset.quizQuestion;
    const max = Number(screen.dataset.quizMax || 1);
    let selected = Array.from(screen.querySelectorAll('[data-quiz-option].is-selected'));

    const wasSelected = option.classList.contains('is-selected');
    const isNoneOption = option.dataset.quizValue === 'none';

    if (wasSelected) {
      option.classList.remove('is-selected');
      option.setAttribute('aria-pressed', 'false');
    } else {
      if (max === 1) {
        selected.forEach((item) => {
          item.classList.remove('is-selected');
          item.setAttribute('aria-pressed', 'false');
        });
      } else if (isNoneOption) {
        selected.forEach((item) => {
          item.classList.remove('is-selected');
          item.setAttribute('aria-pressed', 'false');
        });
      } else {
        const noneOption = screen.querySelector('[data-quiz-option][data-quiz-value="none"].is-selected');

        if (noneOption) {
          noneOption.classList.remove('is-selected');
          noneOption.setAttribute('aria-pressed', 'false');
          selected = selected.filter((item) => item !== noneOption);
        }
      }

      if (!isNoneOption && max > 1 && selected.length >= max) {
        this.setError(screen, `Choose up to ${max}.`);
        return;
      }

      option.classList.add('is-selected');
      option.setAttribute('aria-pressed', 'true');
    }

    const values = Array.from(screen.querySelectorAll('[data-quiz-option].is-selected')).map((item) => item.dataset.quizValue);
    this.answers[questionId] = values;
    this.setError(screen, '');
    this.updateNextButtonState(screen);
    this.saveProgress(this.getCurrentScreenName());
    this.trackAnswerEvent(screen, option, wasSelected ? 'deselect' : 'select');
  }

  nextQuestion() {
    const screen = this.questionScreens[this.currentQuestionIndex];
    if (!this.isQuestionValid(screen)) return;

    this.trackStepComplete(screen);

    if (this.currentQuestionIndex < this.questionScreens.length - 1) {
      this.showQuestion(this.currentQuestionIndex + 1);
      return;
    }

    this.renderResult();
    this.showScreen('result');
    this.saveProgress('result');
  }

  previousQuestion() {
    if (this.currentQuestionIndex === 0) {
      this.showScreen('intro');
      this.saveProgress('intro');
      return;
    }

    this.showQuestion(this.currentQuestionIndex - 1);
  }

  showQuestion(index) {
    this.currentQuestionIndex = index;
    this.updateNextButtonState(this.questionScreens[index]);
    this.showScreen(this.questionScreens[index].dataset.quizScreen);
    this.updateProgress();
    this.saveProgress(this.questionScreens[index].dataset.quizScreen);
    this.trackStepView(this.questionScreens[index]);
  }

  showScreen(screenName) {
    const incoming = this.screens.find((screen) => screen.dataset.quizScreen === screenName);
    const active = this.screens.find((screen) => screen.classList.contains('is-active'));

    if (!incoming || incoming === active) {
      if (incoming) {
        incoming.hidden = false;
        incoming.classList.add('is-active');
      }
      this.dataset.quizCurrentScreen = screenName;
      this.syncMediaHeights();
      return;
    }

    window.clearTimeout(this.transitionTimer);
    window.clearTimeout(this.heightReleaseTimer);
    window.clearTimeout(this.heightSettleTimer);

    this.screens.forEach((screen) => {
      if (screen !== active && screen !== incoming) {
        screen.classList.remove('is-active', 'is-exiting');
        screen.hidden = true;
      }
    });

    const targetHeight = this.measureScreenHeight(incoming, screenName);
    this.lockShellHeight();

    if (active) {
      active.classList.remove('is-active');
      active.classList.add('is-exiting');
    }

    this.transitionTimer = window.setTimeout(() => {
      if (active) {
        active.classList.remove('is-exiting');
        active.hidden = true;
      }

      this.dataset.quizCurrentScreen = screenName;
      incoming.hidden = false;
      incoming.classList.remove('is-exiting');
      this.syncMediaHeights(incoming);
      window.requestAnimationFrame(() => {
        incoming.classList.add('is-active');
        this.syncMediaHeights(incoming);
        this.animateShellToHeight(Math.max(targetHeight, this.shell?.scrollHeight || 0));
        this.releaseShellHeight();
      });
    }, active ? this.transitionOutDuration : 0);

    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  lockShellHeight() {
    if (!this.shell) return;

    this.shell.style.height = `${this.shell.offsetHeight}px`;
  }

  measureScreenHeight(screen, screenName) {
    if (!this.shell || !screen) return this.shell?.offsetHeight || 0;

    const previousScreenName = this.dataset.quizCurrentScreen;
    const wasHidden = screen.hidden;
    const shellStyles = window.getComputedStyle(this.shell);
    const shellPadding = parseFloat(shellStyles.paddingTop || 0) + parseFloat(shellStyles.paddingBottom || 0);
    const progress = this.querySelector('.hormone-smart-quiz__progress');

    this.dataset.quizCurrentScreen = screenName;
    screen.hidden = false;
    screen.classList.add('is-measuring');
    this.syncMediaHeights(screen);

    const progressStyles = progress ? window.getComputedStyle(progress) : null;
    const progressHeight = progress && progressStyles?.display !== 'none'
      ? progress.getBoundingClientRect().height + parseFloat(progressStyles.marginTop || 0) + parseFloat(progressStyles.marginBottom || 0)
      : 0;
    const screenHeight = screen.getBoundingClientRect().height;
    const targetHeight = Math.ceil(shellPadding + progressHeight + screenHeight);

    screen.classList.remove('is-measuring');
    screen.hidden = wasHidden;
    this.dataset.quizCurrentScreen = previousScreenName;

    return targetHeight;
  }

  animateShellToHeight(targetHeight) {
    if (!this.shell || !targetHeight) return;

    this.shell.style.height = `${targetHeight}px`;
  }

  releaseShellHeight() {
    if (!this.shell) return;

    window.clearTimeout(this.heightReleaseTimer);
    window.clearTimeout(this.heightSettleTimer);
    this.heightReleaseTimer = window.setTimeout(() => {
      this.syncMediaHeights();
      this.shell.style.height = `${this.shell.scrollHeight}px`;

      this.heightSettleTimer = window.setTimeout(() => {
        this.shell.style.height = '';
      }, 220);
    }, this.transitionInDuration + 120);
  }

  syncMediaHeights(scope = null) {
    const active = scope || this.screens.find((screen) => screen.classList.contains('is-active'));
    if (!active) return;

    const pairs = active.querySelectorAll('.hormone-smart-quiz__split, .hormone-smart-quiz__result-grid, .hormone-smart-quiz__offer-grid');
    pairs.forEach((pair) => {
      const media = pair.querySelector('.hormone-smart-quiz__media');
      if (!media) return;

      media.style.minHeight = '';
      media.style.height = '';

      if (window.matchMedia('(max-width: 989px)').matches) return;

      const content = Array.from(pair.children).find((child) => child !== media);
      if (!content) return;

      const contentHeight = Math.ceil(content.getBoundingClientRect().height);
      if (contentHeight > 0) {
        media.style.height = `${contentHeight}px`;
      }
    });
  }

  updateProgress() {
    if (!this.progressFill || !this.progressStep) return;

    const step = this.currentQuestionIndex + 1;
    const total = this.questionScreens.length;
    const progress = Math.round((step / total) * 100);
    this.progressFill.style.width = `${progress}%`;
    this.progressStep.textContent = `Step ${step} of ${total}`;
  }

  updateAllNextButtonStates() {
    this.questionScreens.forEach((screen) => this.updateNextButtonState(screen));
  }

  updateNextButtonState(screen) {
    if (!screen) return;

    const min = Number(screen.dataset.quizMin || 1);
    const selected = screen.querySelectorAll('[data-quiz-option].is-selected').length;
    const nextButton = screen.querySelector('[data-quiz-action="next"]');
    if (!nextButton) return;

    nextButton.disabled = selected < min;
  }

  isQuestionValid(screen) {
    const min = Number(screen.dataset.quizMin || 1);
    const selected = screen.querySelectorAll('[data-quiz-option].is-selected').length;

    if (selected < min) {
      this.setError(screen, min === 1 ? 'Please select an answer.' : `Please select at least ${min}.`);
      return false;
    }

    this.setError(screen, '');
    return true;
  }

  setError(screen, message) {
    const error = screen.querySelector('[data-quiz-error]');
    if (!error) return;
    error.textContent = message;
  }

  renderResult(resultKey = this.calculateResult(), options = {}) {
    const { trackCompletion = true } = options;
    const template = this.querySelector(`[data-quiz-result-template="${resultKey}"]`);
    if (!template || !this.resultMount) return;

    this.currentResultKey = resultKey;
    this.resultMount.replaceChildren(template.content.cloneNode(true));
    this.emailForms = Array.from(this.querySelectorAll('[data-quiz-email-form]'));
    this.emailForms.forEach((form) => {
      if (!form.dataset.quizBound) {
        form.addEventListener('submit', this.handleEmailSubmit.bind(this));
        form.dataset.quizBound = 'true';
      }
    });
    this.bindImageLoads(this.resultMount);

    if (this.resultKeyInput) {
      this.resultKeyInput.value = resultKey;
    }

    const popupLead = this.readPopupLead();
    if (popupLead) {
      this.renderKnownEmailSuccess();
    } else {
      this.applyKnownKlaviyoProfile(resultKey, { trackCompletion });
    }

    this.saveProgress('result', resultKey);

    if (!trackCompletion) return;

    this.trackQuizEvent('xomd_quiz_complete', {
      result_key: resultKey,
      answered_questions: Object.keys(this.answers).length
    });

    if (popupLead) {
      this.syncKnownProfileWithKlaviyo(resultKey, { email: popupLead.email, source: 'Klaviyo Quiz Popup' });
    }
  }

  isKlaviyoProfileIdentified() {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (isIdentified) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        resolve(isIdentified === true);
      };
      const timeout = window.setTimeout(() => finish(false), 2000);

      window.klaviyo = window.klaviyo || [];

      if (typeof window.klaviyo.isIdentified === 'function') {
        Promise.resolve(window.klaviyo.isIdentified()).then(finish).catch(() => finish(false));
        return;
      }

      if (typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['isIdentified', finish]);
        return;
      }

      finish(false);
    });
  }

  applyKnownKlaviyoProfile(resultKey, options = {}) {
    const { trackCompletion = true } = options;

    this.knownKlaviyoProfilePromise?.then((isIdentified) => {
      if (
        !isIdentified ||
        this.currentResultKey !== resultKey
      ) {
        return;
      }

      this.renderKnownEmailSuccess();
      this.syncMediaHeights();

      if (trackCompletion) {
        this.syncKnownProfileWithKlaviyo(resultKey, { source: 'Known Klaviyo Profile' });
      }
    });
  }

  readPopupLead() {
    try {
      const rawLead = window.sessionStorage?.getItem(this.popupLeadStorageKey);
      if (!rawLead) return null;

      const lead = JSON.parse(rawLead);
      const capturedAt = Date.parse(lead?.capturedAt);
      const maximumAge = 24 * 60 * 60 * 1000;
      const leadAge = Date.now() - capturedAt;
      const isFresh = Number.isFinite(capturedAt) && leadAge >= 0 && leadAge <= maximumAge;

      if (
        lead?.version !== 1 ||
        typeof lead.email !== 'string' ||
        !lead.email.includes('@') ||
        !isFresh
      ) {
        window.sessionStorage?.removeItem(this.popupLeadStorageKey);
        return null;
      }

      return lead;
    } catch (error) {
      return null;
    }
  }

  renderKnownEmailSuccess() {
    const optIn = this.resultMount?.querySelector('.hormone-smart-quiz__result-opt-in');
    const template = this.querySelector('[data-quiz-known-email-success-template]');
    if (!optIn || !template) return;

    optIn.replaceChildren(template.content.cloneNode(true));
    optIn.dataset.quizKnownEmailSuccess = 'true';
  }

  syncKnownProfileWithKlaviyo(resultKey, options = {}) {
    const { email = '', source = 'Known Klaviyo Profile' } = options;
    const profileValue = this.getKlaviyoProfileValue(resultKey);
    const profileProperties = {
      'Hormone Smart Skin Profile': profileValue
    };
    if (email) {
      profileProperties.email = email;
    }
    const eventProperties = {
      'Hormone Smart Skin Profile': profileValue,
      'Quiz Result Key': resultKey,
      'Answered Questions': Object.keys(this.answers).length,
      'Signup Source': source
    };

    window.klaviyo = window.klaviyo || [];
    if (typeof window.klaviyo.push !== 'function') return;

    window.klaviyo.push(['identify', profileProperties]);
    window.klaviyo.push(['track', 'Hormone Smart Quiz Completed', eventProperties]);
  }

  getKlaviyoProfileValue(resultKey) {
    const profileValues = {
      calmer: 'Calmer',
      rebalancer: 'Rebalancer',
      brightener: 'Brightener',
      recoverer: 'Recoverer',
      optimizer: 'Optimizer'
    };

    return profileValues[resultKey] || resultKey;
  }

  restoreSavedState() {
    const savedState = this.readSavedState();
    if (!savedState) return null;

    this.answers = this.normalizeSavedAnswers(savedState.answers);
    this.applySavedAnswers();

    const savedIndex = Number(savedState.currentQuestionIndex);
    if (Number.isInteger(savedIndex) && savedIndex >= 0 && savedIndex < this.questionScreens.length) {
      this.currentQuestionIndex = savedIndex;
    } else {
      this.currentQuestionIndex = 0;
    }

    const screenName = this.getRestorableScreenName(savedState.screenName);
    const resultKey = this.getRestorableResultKey(savedState.resultKey);

    return { screenName, resultKey };
  }

  readSavedState() {
    try {
      const rawState = window.localStorage?.getItem(this.storageKey);
      if (!rawState) return null;

      const parsedState = JSON.parse(rawState);
      if (parsedState?.version !== this.storageVersion) return null;
      return parsedState;
    } catch (error) {
      return null;
    }
  }

  normalizeSavedAnswers(answers) {
    if (!answers || typeof answers !== 'object') return {};

    return Object.entries(answers).reduce((normalized, [questionId, values]) => {
      if (!Array.isArray(values)) return normalized;

      normalized[questionId] = values
        .filter((value) => typeof value === 'string')
        .slice(0, this.getQuestionMax(questionId));

      return normalized;
    }, {});
  }

  applySavedAnswers() {
    this.querySelectorAll('[data-quiz-option].is-selected').forEach((option) => {
      option.classList.remove('is-selected');
      option.setAttribute('aria-pressed', 'false');
    });

    this.questionScreens.forEach((screen) => {
      const questionId = screen.dataset.quizQuestion;
      const savedValues = this.answers[questionId] || [];

      savedValues.forEach((value) => {
        const option = Array.from(screen.querySelectorAll('[data-quiz-option]'))
          .find((item) => item.dataset.quizValue === value);
        if (!option) return;

        option.classList.add('is-selected');
        option.setAttribute('aria-pressed', 'true');
      });
    });
  }

  saveProgress(screenName = this.getCurrentScreenName(), resultKey = this.resultKeyInput?.value || '') {
    const state = {
      version: this.storageVersion,
      answers: this.answers,
      currentQuestionIndex: this.currentQuestionIndex,
      screenName,
      resultKey,
      updatedAt: new Date().toISOString()
    };

    try {
      window.localStorage?.setItem(this.storageKey, JSON.stringify(state));
    } catch (error) {
      // Storage can be unavailable in some browser modes; the quiz should still work.
    }
  }

  clearSavedState() {
    try {
      window.localStorage?.removeItem(this.storageKey);
    } catch (error) {
      // Storage can be unavailable in some browser modes; the quiz should still work.
    }
  }

  getCurrentScreenName() {
    const active = this.screens.find((screen) => screen.classList.contains('is-active'));
    return active?.dataset.quizScreen || this.dataset.quizCurrentScreen || 'intro';
  }

  getQuestionMax(questionId) {
    const screen = this.questionScreens.find((questionScreen) => questionScreen.dataset.quizQuestion === questionId);
    return Number(screen?.dataset.quizMax || 1);
  }

  getRestorableScreenName(screenName) {
    if (!screenName || !this.screens.some((screen) => screen.dataset.quizScreen === screenName)) {
      return 'intro';
    }

    if (screenName.startsWith('question-')) {
      const questionIndex = this.questionScreens.findIndex((screen) => screen.dataset.quizScreen === screenName);
      this.currentQuestionIndex = questionIndex >= 0 ? questionIndex : this.currentQuestionIndex;
    }

    return screenName;
  }

  getRestorableResultKey(resultKey) {
    if (!resultKey) return '';
    const template = this.querySelector(`[data-quiz-result-template="${resultKey}"]`);
    return template ? resultKey : '';
  }

  bindImageLoads(scope = this) {
    scope.querySelectorAll('img').forEach((image) => {
      if (image.dataset.quizImageBound) return;

      image.dataset.quizImageBound = 'true';
      image.addEventListener('load', () => {
        this.syncMediaHeights();
        if (this.shell?.style.height) {
          this.shell.style.height = `${this.shell.scrollHeight}px`;
        }
      });
    });
  }

  calculateResult() {
    const scores = {
      calmer: 0,
      rebalancer: 0,
      brightener: 0,
      recoverer: 0,
      optimizer: 0
    };

    this.questionScreens.forEach((screen) => {
      screen.querySelectorAll('[data-quiz-option].is-selected').forEach((option) => {
        this.applyScores(scores, option.dataset.quizScores);
      });
    });

    const selectedAge = this.answers.age?.[0];
    const selectedConcerns = this.answers.concerns || [];
    const selectedLifeStage = this.answers.life_stage || [];

    if (selectedLifeStage.includes('perimenopause') || selectedLifeStage.includes('menopause')) {
      scores.rebalancer += 4;
    }

    if (selectedConcerns.includes('dark_spots')) {
      scores.brightener += 4;
    }

    if (selectedConcerns.includes('redness') || selectedConcerns.includes('sensitivity')) {
      scores.calmer += 3;
    }

    if (selectedAge === 'under_30') {
      scores.optimizer += 2;
    }

    const priority = ['rebalancer', 'brightener', 'calmer', 'recoverer', 'optimizer'];
    return priority.reduce((winner, key) => {
      if (scores[key] > scores[winner]) return key;
      return winner;
    }, 'optimizer');
  }

  applyScores(scores, scoreString) {
    if (!scoreString) return;

    scoreString.split(',').forEach((pair) => {
      const [key, rawValue] = pair.split(':').map((item) => item.trim());
      const value = Number(rawValue || 0);

      if (Object.prototype.hasOwnProperty.call(scores, key) && Number.isFinite(value)) {
        scores[key] += value;
      }
    });
  }

  restart() {
    this.trackQuizEvent('xomd_quiz_retake');
    this.clearSavedState();
    this.answers = {};
    this.currentQuestionIndex = 0;
    this.currentResultKey = '';
    this.querySelectorAll('[data-quiz-option].is-selected').forEach((option) => {
      option.classList.remove('is-selected');
      option.setAttribute('aria-pressed', 'false');
    });
    this.querySelectorAll('[data-quiz-error]').forEach((error) => {
      error.textContent = '';
    });
    this.updateAllNextButtonStates();
    this.showScreen('intro');
    this.updateProgress();
  }

  trackStepView(screen) {
    this.trackQuizEvent('xomd_quiz_step_view', this.getQuestionAnalytics(screen));
  }

  trackStepComplete(screen) {
    this.trackQuizEvent('xomd_quiz_step_complete', {
      ...this.getQuestionAnalytics(screen),
      selected_answer_values: this.getSelectedAnswerValues(screen),
      selected_answer_count: screen.querySelectorAll('[data-quiz-option].is-selected').length
    });
  }

  trackAnswerEvent(screen, option, action) {
    this.trackQuizEvent(`xomd_quiz_answer_${action}`, {
      ...this.getQuestionAnalytics(screen),
      answer_value: option.dataset.quizValue || '',
      answer_text: option.textContent.trim(),
      selected_answer_values: this.getSelectedAnswerValues(screen),
      selected_answer_count: screen.querySelectorAll('[data-quiz-option].is-selected').length
    });
  }

  getQuestionAnalytics(screen) {
    const index = this.questionScreens.indexOf(screen);
    const title = screen.querySelector('.hormone-smart-quiz__question-title');

    return {
      question_id: screen.dataset.quizQuestion || '',
      question_step: index + 1,
      question_total: this.questionScreens.length,
      question_text: title?.textContent.trim() || ''
    };
  }

  getSelectedAnswerValues(screen) {
    return Array.from(screen.querySelectorAll('[data-quiz-option].is-selected'))
      .map((option) => option.dataset.quizValue || '')
      .join('|');
  }

  trackQuizEvent(eventName, detail = {}) {
    const payload = {
      event: eventName,
      quiz_id: this.quizId,
      quiz_name: this.quizName,
      ...detail
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    window.dispatchEvent(new CustomEvent('xomd:quiz:event', { detail: payload }));
  }

  handleEmailSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const email = form.querySelector('input[type="email"]');
    const status = form.querySelector('[data-quiz-email-status]');

    if (!email?.checkValidity()) {
      email?.reportValidity();
      return;
    }

    if (status) {
      status.textContent = 'Thanks. Your quiz result is ready to pair with this email capture.';
    }

    form.dataset.quizSubmitted = 'true';
  }
}

if (!customElements.get('hormone-smart-quiz')) {
  customElements.define('hormone-smart-quiz', HormoneSmartQuiz);
}
